import { useState, useRef } from "react";
import { STAGES, PIPELINES, SOURCES, CONTACT_METHODS, ALL_TAGS, PRIORITY_LEVELS, TEAM } from "./constants";
import { S, Ic, Field, Input, Sel, Textarea, Avatar, TagChip } from "./ui";

const INIT = {
  name: "", company: "", contact: "", phone: "", email: "",
  amount: "", probability: "50",
  stage: "New", priority: "Medium",
  pipeline: PIPELINES[0], source: "", sourceOther: "",
  contactMethod: "", startDate: "", endDate: "",
  tags: [], tagOther: "", notes: "",
  assignee: null, assignees: [], inviteEmail: "",
  repeatCustomer: false,
  referralName: "", referralPhone: "", referralRate: "",
  attachments: [],
};

const STEPS = ["Basic Info", "Deal Details", "Tags & Notes", "Attachments"];

export default function CreateOpportunityDrawer({ onClose, onCreate, teamMembers = [] }) {
  const [form, setForm]     = useState(INIT);
  const [step, setStep]     = useState(1);
  const [errors, setErrors] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const fileRef = useRef();

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleTag = tag => setForm(p => ({
    ...p,
    tags: p.tags.some(t => t.label === tag.label)
      ? p.tags.filter(t => t.label !== tag.label)
      : [...p.tags, tag],
  }));

  const handleFiles = e => {
    const files = Array.from(e.target.files).map(file => ({
      name: file.name, size: file.size, type: file.type,
      url: URL.createObjectURL(file), file,
    }));
    setForm(p => ({ ...p, attachments: [...p.attachments, ...files] }));
  };

  const removeFile = idx => setForm(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== idx) }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    const finalSource = form.source === "Other" ? (form.sourceOther || "Other") : form.source;
    const finalTags   = form.tags.map(t => t.label === "Other" ? { ...t, label: form.tagOther || "Other" } : t);
    await onCreate({
      ...form,
      source: finalSource, tags: finalTags,
      amount:       Number(form.amount)       || 0,
      probability:  Number(form.probability)  || 10,
      referralRate: Number(form.referralRate) || 0,
      createdAt: new Date().toISOString(),
    });
  };

  const Div = () => <div style={{ height: "1px", background: "#f0f0f8", margin: "14px 0" }} />;

  // ── Display team members (from DB or local TEAM) ──
  const teamList = teamMembers.length > 0 ? teamMembers : TEAM;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", backdropFilter: "blur(2px)", zIndex: 300, display: "flex", justifyContent: "flex-end" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width: "520px", background: "#fff", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-6px 0 30px rgba(0,0,0,0.14)", animation: "slideInRight .2s ease" }}>

        {/* Header */}
        <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,#1a1a2e,#0f3460)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: "#fff" }}>New Opportunity</div>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", marginTop: "1px" }}>Fill in the details to create</div>
          </div>
          <button onClick={onClose} style={{ width: "26px", height: "26px", border: "none", background: "rgba(255,255,255,0.15)", borderRadius: "7px", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic n="close" s={10} c="#fff" />
          </button>
        </div>

        {/* Step indicator */}
        <div style={{ padding: "10px 20px", borderBottom: "1px solid #f0f0f8", display: "flex", gap: "0", background: "#fafafa", flexShrink: 0, overflowX: "auto" }}>
          {STEPS.map((label, i) => {
            const active = step === i + 1;
            const done   = step > i + 1;
            return (
              <div key={label} style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px", cursor: done ? "pointer" : "default" }} onClick={() => done && setStep(i + 1)}>
                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", flexShrink: 0, background: done ? "#43A047" : active ? "#0f3460" : "#e0e0e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9.5px", fontWeight: "700", color: done || active ? "#fff" : "#9e9eb8" }}>
                    {done ? "✓" : i + 1}
                  </div>
                  <span style={{ fontSize: "10.5px", fontWeight: active ? "600" : "400", color: active ? "#0f3460" : done ? "#43A047" : "#9e9eb8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div style={{ flex: 1, height: "1px", background: done ? "#43A047" : "#e0e0e8", margin: "0 6px" }} />}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

          {/* ── Step 1: Basic Info ── */}
          {step === 1 && (
            <>
              <Field label="Opportunity Name" required>
                <Input value={form.name} onChange={e => { f("name", e.target.value); setErrors(er => ({ ...er, name: undefined })); }}
                  placeholder="e.g. Legal Consulting — Al-Afaq Co."
                  style={errors.name ? { borderColor: "#E53935" } : {}} />
                {errors.name && <span style={{ fontSize: "11px", color: "#E53935", marginTop: "3px", display: "block" }}>{errors.name}</span>}
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Field label="Company">
                  <Input value={form.company} onChange={e => f("company", e.target.value)} placeholder="Company name" />
                </Field>
                <Field label="Contact Person">
                  <Input value={form.contact} onChange={e => f("contact", e.target.value)} placeholder="Full name" />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone} onChange={e => f("phone", e.target.value)} placeholder="+966 5X XXX XXXX" type="tel" />
                </Field>
                <Field label="Email">
                  <Input value={form.email} onChange={e => f("email", e.target.value)} placeholder="email@company.com" type="email" />
                </Field>
              </div>

              <Div />

              <Field label="Pipeline">
                <Sel value={form.pipeline} onChange={e => f("pipeline", e.target.value)}>
                  {PIPELINES.map(p => <option key={p}>{p}</option>)}
                </Sel>
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Field label="Stage">
                  <Sel value={form.stage} onChange={e => f("stage", e.target.value)}>
                    {STAGES.map(s => <option key={s}>{s}</option>)}
                  </Sel>
                </Field>
                <Field label="Priority">
                  <Sel value={form.priority} onChange={e => f("priority", e.target.value)}>
                    {PRIORITY_LEVELS.map(p => <option key={p}>{p}</option>)}
                  </Sel>
                </Field>
              </div>

              {/* Assign To */}
              <Field label="Assign To">
                {teamList.length > 0 ? (
                  <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "8px" }}>
                    {teamList.map(member => {
                      const isSelected = (form.assignees || []).some(m => m.id === member.id);
                      return (
                        <button key={member.id} 
                          onClick={() => {
                            const current = form.assignees || [];
                            const exists = current.some(m => m.id === member.id);
                            const newAssignees = exists 
                              ? current.filter(m => m.id !== member.id)
                              : [...current, member];
                            f("assignees", newAssignees);
                            f("assignee", newAssignees[0] || null);
                          }}
                          style={{ ...S.row(5), padding: "5px 10px", border: `1.5px solid ${isSelected ? member.color : "#e0e0e8"}`, borderRadius: "8px", background: isSelected ? member.color + "15" : "#fff", cursor: "pointer", fontSize: "11.5px", fontWeight: "500", color: isSelected ? member.color : "#3a3a5c", transition: "all .15s" }}>
                          <Avatar user={member} size={18} />
                          {member.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: "10px", background: "#f8f8fc", borderRadius: "8px", fontSize: "12px", color: "#9898b8", textAlign: "center", marginBottom: "8px" }}>
                    No team members yet
                  </div>
                )}
                {/* Invite */}
                {showInvite ? (
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <Input value={form.inviteEmail} onChange={e => f("inviteEmail", e.target.value)} placeholder="email@company.com" type="email" style={{ flex: 1 }} />
                    <button onClick={() => setShowInvite(false)} style={{ padding: "6px 12px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "11.5px", fontWeight: "600" }}>Invite</button>
                    <button onClick={() => setShowInvite(false)} style={{ padding: "6px 8px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "11.5px", color: "#8888a8" }}>Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowInvite(true)} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "5px 10px", border: "1.5px dashed #b0b0c8", borderRadius: "8px", background: "transparent", cursor: "pointer", fontSize: "11.5px", color: "#8888a8" }}>
                    <Ic n="mail" s={11} /> + Invite via Email
                  </button>
                )}
              </Field>

              {/* Repeat Customer */}
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "8px 10px", background: "#f8f8fc", borderRadius: "8px", border: "1px solid #e0e0e8", marginBottom: "10px" }}>
                <input type="checkbox" checked={form.repeatCustomer} onChange={e => f("repeatCustomer", e.target.checked)} style={{ accentColor: "#3761E9", width: "15px", height: "15px" }} />
                <span style={{ fontSize: "12.5px", color: "#3a3a5c", fontWeight: "600" }}>Repeat Customer</span>
                <span style={{ fontSize: "11px", color: "#a0a0b8" }}>— returning client</span>
              </label>

              {/* ── Referral Section ── */}
              <div style={{ background: "#f5f7ff", borderRadius: "10px", border: "1.5px solid #dce3ff", padding: "12px 14px" }}>
                {/* Header toggle */}
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!form.referralName || !!form.referralPhone || !!form.referralRate}
                    onChange={e => {
                      if (!e.target.checked) f("referralName", "") || f("referralPhone", "") || f("referralRate", "");
                    }}
                    style={{ accentColor: "#3761E9", width: "15px", height: "15px" }}
                  />
                  <div>
                    <span style={{ fontSize: "12.5px", color: "#1a1a2e", fontWeight: "700" }}>Referral</span>
                    <span style={{ fontSize: "11px", color: "#8888a8", marginLeft: "6px" }}>— track who referred this deal</span>
                  </div>
                </label>

                {/* Referral fields — always visible for easy entry */}
                <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <Field label="Referrer Name">
                      <Input
                        value={form.referralName}
                        onChange={e => f("referralName", e.target.value)}
                        placeholder="Full name"
                      />
                    </Field>
                    <Field label="Referrer Phone">
                      <Input
                        value={form.referralPhone}
                        onChange={e => f("referralPhone", e.target.value)}
                        placeholder="+966 5X XXX XXXX"
                        type="tel"
                      />
                    </Field>
                  </div>

                  <Field label="Commission Rate (%)">
                    <Input
                      value={form.referralRate}
                      onChange={e => f("referralRate", e.target.value)}
                      placeholder="e.g. 5"
                      type="number"
                      min="0"
                      max="100"
                      style={{ maxWidth: "160px" }}
                    />
                  </Field>

                  {/* Live commission preview */}
                  {form.referralRate && Number(form.referralRate) > 0 && (
                    <div style={{ background: "#1a1a2e", borderRadius: "8px", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px" }}>
                      <div>
                        <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
                          Estimated Commission
                        </div>
                        <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.65)" }}>
                          {form.referralRate}% of contract value
                          {form.amount ? ` (${Number(form.amount).toLocaleString()} SAR)` : ""}
                        </div>
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: "700", color: "#60a5fa" }}>
                        {form.amount
                          ? `${((Number(form.amount) * Number(form.referralRate)) / 100).toLocaleString()} SAR`
                          : "—"}
                      </div>
                    </div>
                  )}

                  {/* Hint when no amount yet */}
                  {form.referralRate && Number(form.referralRate) > 0 && !form.amount && (
                    <div style={{ fontSize: "11px", color: "#8888a8", fontStyle: "italic" }}>
                      💡 Enter a budget in Step 2 to see the commission amount
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Deal Details ── */}
          {step === 2 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Field label="Budget ($)">
                  <Input value={form.amount} onChange={e => f("amount", e.target.value)} placeholder="0.00" type="number" />
                </Field>
                <Field label="Win Probability (%)">
                  <Input value={form.probability} onChange={e => f("probability", e.target.value)} placeholder="50" type="number" min="0" max="100" />
                </Field>
              </div>

              {form.probability && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ height: "6px", background: "#f0f0f8", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${form.probability || 0}%`, background: form.probability >= 70 ? "#43A047" : form.probability >= 40 ? "#FB8C00" : "#E53935", borderRadius: "4px", transition: "width .3s" }} />
                  </div>
                  <span style={{ fontSize: "10.5px", color: "#8888a8" }}>{form.probability}% probability</span>
                </div>
              )}

              <Div />

              <Field label="Source">
                <Sel value={form.source} onChange={e => f("source", e.target.value)}>
                  <option value="">— Select source —</option>
                  {SOURCES.map(s => <option key={s}>{s}</option>)}
                </Sel>
              </Field>

              {/* Source "Other" - custom input */}
              {form.source === "Other" && (
                <Field label="Specify Source">
                  <Input value={form.sourceOther} onChange={e => f("sourceOther", e.target.value)} placeholder="Specify source..." autoFocus />
                </Field>
              )}

              <Field label="Best Contact Method">
                <Sel value={form.contactMethod} onChange={e => f("contactMethod", e.target.value)}>
                  <option value="">— Select method —</option>
                  {CONTACT_METHODS.map(m => <option key={m}>{m}</option>)}
                </Sel>
              </Field>

              <Div />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Field label="Start Date">
                  <Input value={form.startDate} onChange={e => f("startDate", e.target.value)} type="date" />
                </Field>
                <Field label="Estimated Closing Date">
                  <Input value={form.endDate} onChange={e => f("endDate", e.target.value)} type="date" />
                </Field>
              </div>
            </>
          )}

          {/* ── Step 3: Tags & Notes ── */}
          {step === 3 && (
            <>
              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#9898b8", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: "10px" }}>Select Tags</div>
              <div style={{ ...S.row(6, true), marginBottom: "16px" }}>
                {ALL_TAGS.map(tag => {
                  const sel = form.tags.some(t => t.label === tag.label);
                  return (
                    <button key={tag.label} onClick={() => toggleTag(tag)}
                      style={{ padding: "5px 12px", borderRadius: "20px", border: `1.5px solid ${sel ? tag.color : "#e0e0e8"}`, background: sel ? tag.color + "18" : "#fff", color: sel ? tag.color : "#6e6e80", fontSize: "12px", fontWeight: sel ? "700" : "500", cursor: "pointer", transition: "all .15s" }}>
                      {tag.label}
                    </button>
                  );
                })}
              </div>

              {/* Tag "Other" */}
              {form.tags.some(t => t.label === "Other") && (
                <Field label="Specify Tag">
                  <Input value={form.tagOther} onChange={e => f("tagOther", e.target.value)} placeholder="Specify tag..." autoFocus />
                </Field>
              )}

              {form.tags.length > 0 && (
                <div style={{ ...S.row(4, true), marginBottom: "14px" }}>
                  {form.tags.map((tag, i) => <TagChip key={i} tag={tag} onRemove={() => toggleTag(tag)} />)}
                </div>
              )}

              <Div />

              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#9898b8", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: "8px" }}>Notes</div>
              <Textarea value={form.notes} onChange={e => f("notes", e.target.value)} placeholder="Add notes about this opportunity..." rows={5} />
            </>
          )}

          {/* ── Step 4: Attachments ── */}
          {step === 4 && (
            <>
              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#9898b8", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: "12px" }}>File Attachments</div>

              {/* Drop zone */}
              <div
                onClick={() => fileRef.current.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.background = "#f0f4ff"; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = "#c8c8e0"; e.currentTarget.style.background = "#fafafa"; }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = "#c8c8e0"; e.currentTarget.style.background = "#fafafa";
                  const files = Array.from(e.dataTransfer.files).map(file => ({ name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file), file }));
                  setForm(p => ({ ...p, attachments: [...p.attachments, ...files] }));
                }}
                style={{ border: "2px dashed #c8c8e0", borderRadius: "12px", padding: "36px 20px", textAlign: "center", cursor: "pointer", background: "#fafafa", transition: "all .15s", marginBottom: "16px" }}>
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>📎</div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#3761E9", marginBottom: "4px" }}>Click to upload or drag & drop</div>
                <div style={{ fontSize: "11.5px", color: "#a0a0b8" }}>PDF, Word, Excel, Images (max 10MB each)</div>
              </div>
              <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={handleFiles} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />

              {/* File list */}
              {form.attachments.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  {form.attachments.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", background: "#f8f8fc", borderRadius: "9px", border: "1px solid #e0e0e8" }}>
                      <span style={{ fontSize: "18px" }}>
                        {f.type.includes("pdf") ? "📄" : f.type.includes("image") ? "🖼️" : f.type.includes("word") ? "📝" : f.type.includes("excel") || f.type.includes("spreadsheet") ? "📊" : "📎"}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "12.5px", fontWeight: "600", color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        <div style={{ fontSize: "10.5px", color: "#a0a0b8" }}>{(f.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <button onClick={() => removeFile(i)} style={{ border: "none", background: "none", cursor: "pointer", color: "#E53935", fontSize: "14px", padding: "2px" }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: "11px 20px", borderTop: "1px solid #ecedf2", ...S.row(8), justifyContent: "space-between", background: "#fafafa", flexShrink: 0 }}>
          <button onClick={step > 1 ? () => setStep(s => s - 1) : onClose}
            style={{ padding: "8px 14px", border: "1.5px solid #e0e0e8", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600", color: "#6e6e80" }}>
            {step > 1 ? "← Back" : "Cancel"}
          </button>

          {step < STEPS.length ? (
            <button onClick={() => { if (step === 1 && !form.name.trim()) { setErrors({ name: "Required" }); return; } setStep(s => s + 1); }}
              style={{ padding: "8px 18px", border: "none", borderRadius: "8px", background: "linear-gradient(135deg,#1a1a2e,#0f3460)", color: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600", boxShadow: "0 2px 8px rgba(15,52,96,.18)" }}>
              Next →
            </button>
          ) : (
            <button onClick={submit}
              style={{ padding: "8px 18px", border: "none", borderRadius: "8px", background: "linear-gradient(135deg,#1a1a2e,#0f3460)", color: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600", boxShadow: "0 2px 8px rgba(15,52,96,.18)" }}>
              ✓ Create
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
